import type {
    Declaration,
    FundingActivities,
    Invoice,
    SingularPatientInvoice,
    SubTrack,
} from '@common/interfaces/api/fundingProcessApi';
import { DebtorType } from '@common/enums';
import { getSelectedLanguage } from '@frontend/translate';
import type { ActivityRowData } from './FinancingTableActivityRow';
import { canSubTrackBeExported } from '../../../canSubTrackBeExported';
import { FinancingTableActivityHeader } from './FinancingTableActivityHeader';
import { FinancingTableActivityRow } from './FinancingTableActivityRow';

type FinancingTableActivitiesProps = {
    fundingActivities: FundingActivities;
    isExpanded: boolean;
};

export function FinancingTableActivities({ fundingActivities, isExpanded }: FinancingTableActivitiesProps) {
    const {
        fundingErrorCode,
        finance: { financingTypeHeadings },
    } = getSelectedLanguage();

    if (fundingActivities.processActivities.length === 0) {
        return null;
    }

    const invoiceActivityDataRows = fundingActivities.invoices.map(invoiceToActvityDataRow);

    const declarationActivityDataRows = fundingActivities.declarations.flatMap(declarationToActivityDataRows);

    const subTrackDeclarationActivityDataRows = fundingActivities.subTracks
        .filter((subTrack) => subTrack.debtorType === DebtorType.insurer)
        .flatMap(subTrackToActivityDataRows);
    declarationActivityDataRows.push(...subTrackDeclarationActivityDataRows);

    const subTrackPatientInvoiceActivityDataRows = fundingActivities.subTracks
        .filter((subTrack) => subTrack.debtorType === DebtorType.patient)
        .flatMap(subTrackToActivityDataRows);

    const SingularPatientInvoiceActivityDataRows = fundingActivities.singularPatientInvoices.map(
        singularPatientInvoiceToActivityDataRow
    );

    return (
        <>
            {invoiceActivityDataRows.length > 0 && showActivityDataRowHeader('invoices')}
            {showFinancingTableActivityRows(invoiceActivityDataRows)}

            {declarationActivityDataRows.length > 0 && showActivityDataRowHeader('declarations')}
            {showFinancingTableActivityRows(declarationActivityDataRows)}

            {subTrackPatientInvoiceActivityDataRows.length > 0 && showActivityDataRowHeader('compositePatientInvoices')}
            {showFinancingTableActivityRows(subTrackPatientInvoiceActivityDataRows)}

            {SingularPatientInvoiceActivityDataRows.length > 0 && showActivityDataRowHeader('singularPatientInvoices')}
            {showFinancingTableActivityRows(SingularPatientInvoiceActivityDataRows)}
        </>
    );

    function showActivityDataRowHeader(financingTypeHeading: keyof typeof financingTypeHeadings) {
        const heading = financingTypeHeadings[financingTypeHeading];

        return (
            <FinancingTableActivityHeader
                heading={heading}
                isExpanded={isExpanded}
            />
        );
    }

    function showFinancingTableActivityRows(rows: ActivityRowData[]) {
        return rows.map((row) => (
            <FinancingTableActivityRow
                data={row}
                isExpanded={isExpanded}
                key={row.id}
            />
        ));
    }

    function invoiceToActvityDataRow(invoice: Invoice): ActivityRowData {
        const batch = invoice.batches?.[0] || undefined;

        return {
            activityCode: null,
            amount: invoice.amount,
            careProductType: null,
            id: invoice.id,
            isCounterBooking: Boolean(invoice.counterBookingId),
            notFundedReason: null,
            performedDate: invoice.invoiceDate,
            providerOrganization: invoice.providerOrganization,
            relationNumber: invoice.relationNumber,
            requesterGroup: invoice.requesterGroup,
            runNumber: batch?.runNumber ?? null,
        };
    }

    function declarationToActivityDataRows(declaration: Declaration): ActivityRowData[] {
        const batch = declaration.batches?.[0] || undefined;

        return declaration.declarationActivities.map((declarationActivity) => {
            return {
                activityCode: declarationActivity.activityCode,
                amount: declarationActivity.amount,
                careProductType: declaration.careProductType,
                id: declarationActivity.id,
                isCounterBooking: declaration.counterBookedDeclarationId !== null,
                notFundedReason: null,
                performedDate: declarationActivity.activityDate,
                providerOrganization: null,
                relationNumber: null,
                requesterGroup: null,
                runNumber: batch?.runNumber ?? null,
            };
        });
    }

    function subTrackToActivityDataRows(subTrack: SubTrack): ActivityRowData[] {
        const batch = subTrack.batches?.[0] || undefined;

        const notFundedReason =
            canSubTrackBeExported(subTrack) && subTrack.gateSpecialistAgbCode === null
                ? fundingErrorCode.missingPoortfunctionaris
                : null;

        return (subTrack.careActivities || []).map((careActivity) => {
            return {
                activityCode: careActivity.activityCode,
                amount: careActivity.supplementPriceActivity,
                careProductType: subTrack.careProductType,
                id: careActivity.id,
                isCounterBooking: subTrack.isCounterBooked,
                notFundedReason,
                performedDate: careActivity.performedDate,
                providerOrganization: null,
                relationNumber: null,
                requesterGroup: null,
                runNumber: batch?.runNumber ?? null,
            };
        });
    }

    function singularPatientInvoiceToActivityDataRow(invoice: SingularPatientInvoice): ActivityRowData {
        const batch = invoice.batches?.[0] || undefined;

        return {
            activityCode: null,
            amount: invoice.amount,
            careProductType: null,
            id: invoice.id,
            isCounterBooking: Boolean(invoice.counterBookingId),
            notFundedReason: null,
            performedDate: invoice.activityDate,
            providerOrganization: null,
            relationNumber: null,
            requesterGroup: null,
            runNumber: batch?.runNumber ?? null,
        };
    }
}
